import React, { useRef, useState, useEffect } from "react";
import { Nav, Navbar } from "reactstrap";
import Scrollbar from "react-scrollbars-custom";
import { Link } from "react-router-dom";
import SideBarMenu from "./menu";
import { hamburgerIcon } from "./icons";
import "./style.css";
import Flex from "../../components/common/Flex";
import logo from "../../assets/img/illustrations/falcon.png";
import { navbarBreakPoint } from "../../config";

const SideBar = ({ onStateChange }) => {
  const sideBarRef = useRef(null);
  const sidebarWrapperRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      setIsMobile(mobile);
      if (width < 1200) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (onStateChange) {
      // Calculate effective hovered state based on both hovered and collapsed values
      const effectiveHovered = isMobile ? hovered : (hovered || (isMobile && !collapsed));
      onStateChange(collapsed, effectiveHovered);
    }
  }, [collapsed, hovered, isMobile, onStateChange]);

  useEffect(() => {
    if (isMobile && (hovered || !collapsed)) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }

    return () => {
      document.body.classList.remove("sidebar-open");
    };
  }, [hovered, collapsed, isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && hovered && sidebarWrapperRef.current && !sidebarWrapperRef.current.contains(event.target)) {
        setHovered(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hovered, isMobile]);

  var forgott_style;
  if (window.location.pathname === "/ForgotPassword") {
    forgott_style = "none";
  }

  const sidebarClass = `navbar-vertical sidebar-expanded ${
    collapsed ? "sidebar-collapsed" : ""
  } ${hovered ? "sidebar-hovered" : ""}`;

  const handleMouseEnter = () => {
    // Only use hover effect in desktop mode when already collapsed
    if (collapsed && !isMobile) {
      setHovered(true);
    }
  };

  const handleMouseLeave = () => {
    // Only use hover effect in desktop mode
    if (!isMobile) {
      setHovered(false);
    }
  };

  const handleBackdropClick = () => {
    if (isMobile) {
      // When clicking backdrop, collapse the sidebar
      setHovered(false);
      setCollapsed(true);
    }
  };

  const handleMenuItemClick = () => {
    if (isMobile) {
      // When clicking menu item, collapse the sidebar
      setHovered(false);
      setCollapsed(true);
    }
  };

  const toggleSidebar = () => {
    if (isMobile) {
      // Toggle both states at once to avoid race conditions
      const newHoveredState = !hovered;
      setHovered(newHoveredState);
      setCollapsed(!newHoveredState);
    }
  };
  
  // Remove the useEffect that was causing the loop

  return (
    <div ref={sidebarWrapperRef}>
      {/* Show backdrop when sidebar is expanded in mobile */}
      {isMobile && (
        <div
          className={`sidebar-backdrop ${hovered ? 'visible' : 'hidden'}`}
          onClick={handleBackdropClick}
          style={{
            opacity: hovered ? 1 : 0,
            visibility: hovered ? 'visible' : 'hidden',
            transition: 'opacity 0.8s cubic-bezier(0.25, 1, 0.3, 1), visibility 0.8s cubic-bezier(0.25, 1, 0.3, 1)'
          }}
        ></div>
      )}
      <Navbar
        expand={navbarBreakPoint}
        className={sidebarClass}
        light
        style={{ 
          paddingLeft: collapsed ? '0' : '1rem',
          display: forgott_style,
          transition: 'transform 0.8s cubic-bezier(0.25, 1, 0.3, 1), width 0.8s cubic-bezier(0.25, 1, 0.3, 1), min-width 0.8s cubic-bezier(0.25, 1, 0.3, 1)',
          ...(isMobile && collapsed && !hovered ? {
            transform: 'translateX(0)', 
            width: '60px',
            minWidth: '60px'
          } : {})
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Flex align="center" className="sidebar-logo-container">
          <Link 
            to={isMobile ? "#" : "/"}
            onClick={(e) => {
              if (isMobile) {
                e.preventDefault();
                // Only toggle if we're in collapsed state 
                if (collapsed && !hovered) {
                  toggleSidebar();
                }
              }
            }}
          >
            {isMobile && collapsed && !hovered ? (
              <div className="hamburger-icon-wrapper">
                {hamburgerIcon()}
              </div>
            ) : (
              <img
                src={logo}
                alt="Logo"
                width={120}
                id="sideBarLogo"
                className={`sidebar-logo ${isMobile ? 'mobile-logo' : ''}`}
              />
            )}
          </Link>
          {/* Always show close button when sidebar is expanded in mobile */}
          {isMobile && hovered && (
            <div className="mobile-toggle-btn" onClick={toggleSidebar}>
              <span className="toggle-icon open"></span>
            </div>
          )}
        </Flex>

        <div 
          className="sidebar-content bg-200" 
          ref={sideBarRef}
          onClick={(e) => {
            // In mobile collapsed mode, clicking anywhere expands the sidebar BUT only if we're not clicking on a navigation link without children
            if (isMobile && collapsed && !hovered) {
              const navLink = e.target.closest('.nav-link');
              const dropdownIndicator = e.target.closest('.dropdown-indicator');
              // If clicking on a nav-link (direct navigation), don't expand sidebar
              if (navLink && !dropdownIndicator) {
                return;
              }
              // If clicking on dropdown indicator or empty space, expand sidebar
              e.preventDefault();
              e.stopPropagation();
              setHovered(true);
              setCollapsed(false);
            }
          }}
        >
          <Scrollbar
            style={{ 
              height: isMobile && hovered ? "calc(100vh - 110px)" : "calc(100vh - 77px)", 
              display: "block",
              transition: "height 0.8s cubic-bezier(0.25, 1, 0.3, 1)"
            }}
            trackYProps={{
              renderer(props) {
                const { elementRef, ...restProps } = props;
                return (
                  <span {...restProps} ref={elementRef} className="TrackY" />
                );
              },
            }}
          >
            <Nav navbar vertical>
              <SideBarMenu collapsed={collapsed} onMenuItemClick={handleMenuItemClick} />
            </Nav>
          </Scrollbar>
          
          {/* Mobile expanded logo at the bottom */}
          {isMobile && hovered && (
            <div className="mobile-bottom-logo">
              <img 
                src={logo} 
                alt="Logo" 
                className="bottom-sidebar-logo" 
              />
            </div>
          )}
        </div>
      </Navbar>
    </div>
  );
};

export default SideBar;
